import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

import { getCustStatistik } from "../services/dashboardService";

const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];

const MONTHS = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

export default function CustomerDashboard() {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState({
    selectedYear: currentYear,
    selectedMonth: new Date().getMonth() + 1,

    leaderBoard: [],
    monthlyLeaderBoard: [],

    customerNow: [],
    customerGrowth: [],
    customerDistribution: [],
    customerType: [],
  });

  const loadData = async () => {
    try {
      setLoading(true);

      const result = await getCustStatistik(year, month);

      setData({
        selectedYear: result?.selectedYear || year,

        selectedMonth: result?.selectedMonth || month,

        leaderBoard: result?.leaderBoard || [],

        monthlyLeaderBoard: result?.monthlyLeaderBoard || [],

        customerNow: result?.customerNow || [],

        customerGrowth: result?.customerGrowth || [],

        customerDistribution: result?.customerDistribution || [],

        customerType: result?.customerType || [],
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [year, month]);

  const customerStatusMap = Object.fromEntries(
    data.customerNow.map((item) => [item.status, Number(item.jumlah)])
  );

  const growthData = {};

  // init 12 bulan biar chart stabil
  for (let i = 1; i <= 12; i++) {
    growthData[i] = {
      bulan: MONTHS[i],
      active: 0,
      closed: 0,
    };
  }

  // isi dari backend
  data.customerGrowth.forEach((item) => {
    const month = item.bulan;

    if (growthData[month]) {
      growthData[month][item.tipe] = item.jumlah;
    }
  });

  // convert ke array
  const growthChartData = Object.values(growthData);

  const leaderBoardData = [...data.leaderBoard]
    .sort((a, b) => b.jumlah - a.jumlah)
    .map((item, index) => ({
      ...item,
      ranking: index + 1,
    }));

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded"></div>
          <div className="h-80 bg-slate-200 dark:bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-slate-100 dark:bg-slate-900">
      {/* HEADER */}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Customer Dashboard
          </h1>

          <p className="text-slate-500 dark:text-slate-400">
            Customer statistics overview
          </p>
        </div>

        <div className="flex gap-3">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border rounded-lg px-4 py-2 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-white"
          >
            <option value={1}>January</option>
            <option value={2}>February</option>
            <option value={3}>March</option>
            <option value={4}>April</option>
            <option value={5}>May</option>
            <option value={6}>June</option>
            <option value={7}>July</option>
            <option value={8}>August</option>
            <option value={9}>September</option>
            <option value={10}>October</option>
            <option value={11}>November</option>
            <option value={12}>December</option>
          </select>

          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border rounded-lg px-4 py-2 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-white"
          >
            {[2024, 2025, 2026].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI */}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {["active", "inactive", "suspended", "closed", "terminated"].map(
          (status) => (
            <div
              key={status}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-5"
            >
              <div className="text-sm text-slate-500 capitalize">{status}</div>

              <div className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                {customerStatusMap[status] ?? 0}
              </div>
            </div>
          )
        )}
      </div>

      {/* CUSTOMER GROWTH */}

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-5 mb-6">
        <h2 className="font-semibold text-slate-900 dark:text-white mb-4">
          Customer Growth / Pertumbuhan Pelanggan
        </h2>

        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={growthChartData}>
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="bulan" />

            <YAxis />

            <Tooltip />

            <Legend />

            <Line
              type="monotone"
              dataKey="active"
              stroke="#3b82f6"
              strokeWidth={3}
            />

            <Line
              type="monotone"
              dataKey="closed"
              stroke="#ef4444"
              strokeWidth={3}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ROW 2 */}

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* DISTRIBUTION */}

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-5">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-4">
            Customer Distribution / Penyebaran Pelanggan
          </h2>

          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data.customerDistribution}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="city" />

              <YAxis />

              <Tooltip />

              <Bar dataKey="jumlah" fill="#3b82f6" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* CUSTOMER TYPE */}

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-5">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-4">
            Customer Type / Tipe Pelanggan
          </h2>

          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={data.customerType}
                dataKey="jumlah"
                nameKey="residence_type"
                outerRadius={120}
                label
              >
                {data.customerType.map((item, index) => (
                  <Cell
                    key={item.residence_type}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>

              <Tooltip />

              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* LEADER BOARD */}

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-5 mb-6">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="font-semibold text-slate-900 dark:text-white">
              Monthly Leader Board
            </h2>

            <p className="text-sm text-slate-500">
              Aktivasi pelanggan baru bulan ini
            </p>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            layout="vertical"
            data={data.monthlyLeaderBoard}
            margin={{
              top: 5,
              right: 40,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis type="number" />

            <YAxis type="category" dataKey="groupname" width={150} />

            <Tooltip />

            <Bar dataKey="jumlah" fill="#3b82f6" radius={[0, 8, 8, 0]}>
              <LabelList dataKey="jumlah" position="right" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto mt-6">
        <table className="min-w-full">
          <thead>
            <tr className="border-b dark:border-slate-700">
              <th className="text-left p-3">Rank</th>

              <th className="text-left p-3">Referral</th>

              <th className="text-right p-3">This Month</th>

              <th className="text-right p-3">Last Month</th>

              <th className="text-right p-3">Growth</th>
            </tr>
          </thead>

          <tbody>
            {data.monthlyLeaderBoard.map((item, index) => (
              <tr
                key={item.groupname}
                className="border-b dark:border-slate-700"
              >
                <td className="py-3 px-4">
                  {index + 1 === 1 && "🥇"}
                  {index + 1 === 2 && "🥈"}
                  {index + 1 === 3 && "🥉"}

                  {index + 1 > 3 && `#${index}`}
                </td>

                <td className="p-3">{item.groupname}</td>

                <td className="p-3 text-right font-semibold">{item.jumlah}</td>

                <td className="p-3 text-right">{item.previous_month}</td>

                <td className="p-3 text-right">
                  <span
                    className={
                      item.mtd_growth >= 0
                        ? "text-green-600 font-semibold"
                        : "text-red-600 font-semibold"
                    }
                  >
                    {item.mtd_growth >= 0 ? "+" : ""}
                    {item.mtd_growth}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <br />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Card 1 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 transition-colors shadow-sm">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Top Referral
          </div>

          <div className="text-xl font-bold text-slate-900 dark:text-white">
            {leaderBoardData[0]?.groupname || "-"}
          </div>
        </div>

        {/* Card 2 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 transition-colors shadow-sm">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Active Customer
          </div>

          <div className="text-xl font-bold text-slate-900 dark:text-white">
            {leaderBoardData[0]?.jumlah || 0}
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 transition-colors shadow-sm">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Referral Groups
          </div>

          <div className="text-xl font-bold text-slate-900 dark:text-white">
            {leaderBoardData.length}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-slate-900 dark:text-white">
              All Time Leader Board
            </h2>

            <p className="text-sm text-slate-500 dark:text-slate-400">
              Top referral performance
            </p>
          </div>

          <div className="text-sm text-slate-500">
            Total : {leaderBoardData.length}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={650}>
          <BarChart
            layout="vertical"
            data={leaderBoardData}
            margin={{
              top: 10,
              right: 40,
              left: 20,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis type="number" allowDecimals={false} />

            <YAxis dataKey="groupname" type="category" width={180} />

            <Tooltip formatter={(value) => [value, "Active Customer"]} />

            <Bar dataKey="jumlah" fill="#10b981" radius={[0, 8, 8, 0]}>
              <LabelList dataKey="jumlah" position="right" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b dark:border-slate-700">
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">
                Rank
              </th>

              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">
                Group
              </th>

              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">
                Active Customer
              </th>
            </tr>
          </thead>

          <tbody>
            {leaderBoardData.map((item) => (
              <tr
                key={item.referred_by}
                className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <td className="py-3 px-4">
                  {item.ranking === 1 && "🥇"}
                  {item.ranking === 2 && "🥈"}
                  {item.ranking === 3 && "🥉"}

                  {item.ranking > 3 && `#${item.ranking}`}
                </td>

                <td className="py-3 px-4 text-slate-900 dark:text-white">
                  {item.groupname}
                </td>

                <td className="py-3 px-4 text-right font-semibold text-slate-900 dark:text-white">
                  {item.jumlah}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
